import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { useEffect } from 'react';
import Button from '@alltrails/shared/denali/components/Button';
import Typography from '@alltrails/shared/denali/components/Typography';
import logExploreWithProButtonClicked from '@alltrails/analytics/events/logExploreWithProButtonClicked';
import logProWelcomePageViewed from '@alltrails/analytics/events/logProWelcomePageViewed';
import CustomProvider from 'components/CustomProvider';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import ApplicationLayout from 'components/ApplicationLayout';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import { Context } from '@alltrails/shared/types/context';
import PlusBadge from '@alltrails/shared/denali/components/PlusBadge';
import * as styles from './PlusWelcome.module.scss';
import collageMobileAvif from './images/collageMobile.avif';
import collageMobileWebp from './images/collageMobile.webp';
import collageMobileJpg from './images/collageMobile.jpg';
import collageJpg from './images/collage.jpg';
import collageAvif from './images/collage.avif';
import collageWebp from './images/collage.webp';

const br = () => <br />;

// https://www.figma.com/file/LF38o2YcGyX1IXYdjWzhnM/Plus-Welcome-LP-Imagery

function PlusWelcome() {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    logProWelcomePageViewed({});
  }, []);

  const buttonDestination = wrapUrlSafe(user ? '/explore' : '/login', languageRegionCode);

  return (
    <CustomProvider>
      <div className={styles.content}>
        <div className={styles.welcome}>
          {/* Add mobile & desktop badges to prevent flicker */}
          <div className={styles.mobileBadge}>
            <PlusBadge size="lg" />
          </div>
          <div className={styles.desktopBadge}>
            <PlusBadge size="xl" />
          </div>

          <Typography component="h1" variant="heading700">
            <FormattedMessage defaultMessage="Welcome to AllTrails+" />
          </Typography>
          <Typography variant="paragraph300">
            <FormattedMessage
              defaultMessage="You can now do even more outdoors with extra safety and planning<br></br> features like offline maps, 3D views, and wrong-turn alerts."
              values={{ br }}
            />
          </Typography>
          <Button
            className={styles.btn}
            text={<FormattedMessage defaultMessage="Start exploring" />}
            linkInfo={{ href: buttonDestination }}
            onClick={() => logExploreWithProButtonClicked()}
            testId="pro-welcome-start-exploring"
            variant="primary"
            size="lg"
          />
          <picture>
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={collageMobileAvif} type="image/avif" />
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={collageMobileWebp} type="image/webp" />
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={collageMobileJpg} type="image/jpg" />
            <source srcSet={collageAvif} type="image/avif" />
            <source srcSet={collageWebp} type="image/webp" />
            <img
              className={styles.img}
              src={collageJpg}
              alt={intl.formatMessage({
                defaultMessage:
                  'Three images depicting two people walking up a set of stairs on a leafy path, a person smiling, and two people on a hill'
              })}
            />
          </picture>
        </div>
      </div>
    </CustomProvider>
  );
}

type Props = {
  context: Context;
  currentPage: string;
  locationSlug: string;
};

export default function Wrapper({ context, currentPage, locationSlug }: Props) {
  return (
    <ApplicationLayout context={context} currentPage={currentPage} locationSlug={locationSlug}>
      <CustomProvider>
        <PlusWelcome />
      </CustomProvider>
    </ApplicationLayout>
  );
}
